import React, { useEffect, useRef, useState } from "react";
import "./customer-bulk-draft.style.scss";
import "../invoice-form/invoice-form.style.scss";
import { useNavigate } from "react-router-dom";

// form handle
import { useFormik } from "formik";
import * as yup from "yup";

import { formatEnDate } from "../../global/date.util";

// global functions and strings
import { isEmptyObject, rupeeConverter } from "../../global/function";
import strings from "../../global/strings";

// components
import InputDatePicker from "../input-date-picker/input-date-picker.component";
import SelectBox from "../form-inputs/select-box.component";
import InvoiceItemsFooter from "../invoice-form/invoice-items-footer.component";
import EmptyRecord from "../empty-record/empty-record.component";
import FormError from "../form-error/form-error.component";
import SuccessDisplay from "../success-display/success-display.component";
import StructuredSideModal from "../structured-side-modal/structured-side-modal.component";
import { Button } from "../ui/button";

// queries
import { useCompanyDetails } from "../../queries/company.queries";
import { useFilteredCustomers, Customer } from "../../queries/customer.queries";
import { useSaveDraft, DraftPayload } from "../../queries/drafts.queries";
import type { BilledItem } from "../invoice-template/invoice-template.component";
import { useBillTotals } from "../../hooks/useBillTotals";

// icons
import {
	FaCheck,
	FaFileCirclePlus,
	FaFloppyDisk,
	FaPhone,
} from "react-icons/fa6";

interface CustomerBulkDraftProps {
	isOpen: boolean;
	onDraftCreateModalClose: () => void;
	// Superset of CustomerFilters (customer-list.page.tsx's filter form also
	// carries ad hoc keys like total_sales_min/max) — see useFilteredCustomers.
	filterValues: Record<string, unknown>;
}

interface DraftFormValues {
	invoice_date: string;
	sales_date: string;
	payment_mode: string;
	sub_total: number;
	taxable_total_sales: number;
	non_taxable_total_sales: number;
	taxable_sales_vat: number;
	non_taxable_amt: number;
	vat: number;
	total_sales: number;
	taxable_service_charge: number | string;
	non_taxable_service_charge: number | string;
	amount_for_esf: number;
	esf: number;
	taxable_discount: number | string;
	non_taxable_discount: number | string;
	billed_items: BilledItem[];
	sale_info: string;
}

function CustomerBulkDraft({
	isOpen,
	onDraftCreateModalClose,
	filterValues,
}: CustomerBulkDraftProps) {
	const { data: companyData } = useCompanyDetails();
	const preferenceDetails = companyData?.company?.preference;

	const { data: filteredCustomersData } = useFilteredCustomers(filterValues);
	const filteredCustomers = filteredCustomersData ?? [];

	const saveDraftMutation = useSaveDraft();
	const isLoading = saveDraftMutation.isPending;

	// Brief SuccessDisplay before navigating away — replaces the former
	// useSaveDraft toast, which had nothing to show once navigation happened
	// on the same tick. Same ~1.2s pattern as the profile modals' add/update
	// success flash, just gating a navigate() instead of a modal close.
	const navigateTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	useEffect(() => {
		return () => {
			if (navigateTimeoutRef.current) clearTimeout(navigateTimeoutRef.current);
		};
	}, []);

	const [clientsToAddDraft, setClientsToAddDraft] = useState<
		Record<number, Customer>
	>({});

	const [hideDraftDetails, setHideDraftDetails] = useState(false);

	const navigate = useNavigate();

	// bill footer function start

	const customerDraftSchema = () => {
		return yup.object().shape({
			payment_mode: yup
				.string()
				.nullable()
				.required("Choose the payment method"),
			sub_total: yup
				.number()
				.typeError("Must be a number")
				.required("Required"),
			billed_items: yup.array().of(
				yup.object().shape({
					count: yup
						.number()
						.typeError("Must be a number")
						.nullable(),
					item_name: yup
						.string()
						.nullable()
						.max(50, "Item name must be at most 50 characters")
						.required("Required"),
					item_price: yup
						.number()
						.typeError("Must be a number")
						.nullable()
						.required("Required")
						.positive("Required"),
					item_qty: yup
						.number()
						.typeError("Must be a number")
						.nullable()
						.required("Required")
						.positive("Required"),
					total_price: yup
						.number()
						.typeError("Must be a number")
						.nullable(),
				})
			),
			taxable_discount: yup.number().typeError("Must be a number"),
			non_taxable_discount: yup.number().typeError("Must be a number"),
		});
	};

	const formik = useFormik<DraftFormValues>({
		validationSchema: customerDraftSchema,
		initialValues: {
			invoice_date: formatEnDate(new Date(), "YYYY-MM-DD"),
			sales_date: formatEnDate(new Date(), "YYYY-MM-DD"),
			payment_mode: "",
			sub_total: 0,
			taxable_total_sales: 0,
			non_taxable_total_sales: 0,
			taxable_sales_vat: 0,
			non_taxable_amt: 0,
			vat: 0,
			total_sales: 0, // totalSales
			taxable_service_charge: 0,
			non_taxable_service_charge: 0,
			amount_for_esf: 0,
			esf: 0,
			taxable_discount: 0,
			non_taxable_discount: 0,
			billed_items: [
				{
					hs_code: "",
					item_name: "",
					item_price: 0,
					item_qty: 0,
					total_price: 0,
					vatable: !!preferenceDetails?.vat || (preferenceDetails?.esf ?? 0) > 0,
				},
			],
			sale_info: "",
		},
		onSubmit: async () => {
			setHideDraftDetails(true);
		},
	});

	const {
		isServiceChargeEnabled,
		handleDiscountServiceChange,
		handleBilledItemsChange,
		handleServiceChargeToggle,
	} = useBillTotals({ formik, preferenceDetails });

	const handleInvoiceDateChange = (fieldName: string, fieldValue: string) => {
		formik.setFieldValue(fieldName, fieldValue);
	};

	// bill footer function end

	const handleClientSelection = (customer: Customer, index: number) => {
		let state = { ...clientsToAddDraft };
		if (state[index]) {
			delete state[index];
		} else {
			state[index] = customer;
		}
		setClientsToAddDraft(state);
	};

	const handleToggleAll = () => {
		let objState: Record<number, Customer>;
		if (
			Object.keys(clientsToAddDraft).length === filteredCustomers.length
		) {
			objState = {};
		} else {
			objState = filteredCustomers.reduce(
				(acc: Record<number, Customer>, value, index) => {
					acc[index] = value;
					return acc;
				},
				{}
			);
		}
		setClientsToAddDraft(objState);
	};

	const handleDraftsCreate = async () => {
		if (!isLoading) {
			const formError = await formik.validateForm(); // Validate the form
			if (isEmptyObject(formError) && !isEmptyObject(clientsToAddDraft)) {
				const draftArray: DraftPayload[] = Object.values(
					clientsToAddDraft
				).map((item) => {
					const draftObj: DraftPayload & Record<string, unknown> = {
						...item,
						bill_for: item?.customer_tag || "",
						draft_valid: true,
						buyer_pan: item?.customer_pan_vat?.toString() || null,
						buyer_name: item?.customer_name,
						...formik.values,
						non_taxable_discount: parseFloat(
							String(formik.values?.non_taxable_discount || 0)
						),
						taxable_discount: parseFloat(
							String(formik.values?.taxable_discount || 0)
						),
						non_taxable_service_charge: parseFloat(
							String(formik.values?.non_taxable_service_charge || 0)
						),
						taxable_service_charge: parseFloat(
							String(formik.values?.taxable_service_charge || 0)
						),
						amount_for_esf: parseFloat(
							String(formik.values?.amount_for_esf || 0)
						),
						esf: parseFloat(String(formik.values?.esf || 0)),
					};
					delete draftObj.createdAt;
					delete draftObj.updatedAt;

					return draftObj;
				});

				try {
					await saveDraftMutation.mutateAsync({
						drafts: draftArray,
						draft_bill_status: "draft",
					});
					navigateTimeoutRef.current = setTimeout(() => {
						navigate("/invoice/create");
					}, 1200);
				} catch {
					// FormError (bound to saveDraftMutation) renders the message
				}
			} else {
				formik.submitForm();
				setHideDraftDetails(false);
			}
		}
	};

	return (
		<StructuredSideModal
			open={isOpen}
			onOpenChange={(open) => { if (!open) onDraftCreateModalClose(); }}
			title="Create Drafts"
			width={560}
			isDirty={!saveDraftMutation.isSuccess && (formik.dirty || !isEmptyObject(clientsToAddDraft))}
			onSubmit={(e) => { e.preventDefault(); handleDraftsCreate(); }}
			footer={!saveDraftMutation.isSuccess && (
				<div className="flex justify-end">
					<Button
						size="sm"
						type="submit"
						disabled={isEmptyObject(clientsToAddDraft) || isLoading}
					>
						<FaFileCirclePlus />
						Create Draft
					</Button>
				</div>
			)}
		>
		<div className="clients-draft-container">
			{saveDraftMutation.isSuccess ? (
				<SuccessDisplay
					title="Draft(s) created"
					description="Selected clients' drafts have been created."
				/>
			) : (
			<div className="modal-body">
				<FormError
					isError={saveDraftMutation.isError}
					error={saveDraftMutation.error}
					fallback="Something went wrong. Please try again."
				/>
				<div className="client-filters-wrapper">
					<h2 className="section-header">Filter Values</h2>
					<div className="client-filter-container">
						{!isEmptyObject(filterValues) ? (
							Object.values(filterValues).map((value, i) => (
								<div
									key={i}
									className="client-filter-container-inner"
								>
									<span>{String(value)}</span>
								</div>
							))
						) : (
							<div className="section-description">
								No Filter Applied
							</div>
						)}
					</div>
				</div>
				<div className="divider" />

				{Array.isArray(filteredCustomers) &&
				filteredCustomers.length > 0 ? (
					<div className="client-draft-wrapper">
						<h2 className="section-header">Filtered Clients</h2>
						<p className="section-description">
							Select all or some of the filtered bills to
							duplicate.
						</p>
						<div
							className="client-container"
							onClick={handleToggleAll}
						>
							<div
								className={`client-select ${
									Object.keys(clientsToAddDraft).length ===
										filteredCustomers.length && "selected"
								}`}
							>
								{Object.keys(clientsToAddDraft).length ===
									filteredCustomers.length && (
									<FaCheck
										style={{
											color: "#46BE46",
											fontSize: "12px",
										}}
									/>
								)}
							</div>
							<div className="select-btn">
								{Object.keys(clientsToAddDraft).length ===
								filteredCustomers.length
									? "Deselect All"
									: "Select All"}
							</div>
						</div>
						{filteredCustomers.map((customer, i) => (
							<div
								className="client-container"
								key={i}
								onClick={() =>
									handleClientSelection(customer, i)
								}
							>
								<div
									className={`client-select ${
										clientsToAddDraft[i] && "selected"
									}`}
								>
									{clientsToAddDraft[i] && (
										<i
											className="fa-solid fa-check"
											style={{
												color: "#46BE46",
												fontSize: "12px",
											}}
										></i>
									)}
								</div>

								<div className="client-container-inner">
									<div className="bill-details">
										<h4 className="client-maintext">
											{customer?.customer_name}
										</h4>
										<div className="client-subtext">
											<FaPhone />
											<p>{customer?.customer_mobile}</p>
										</div>
									</div>
									<div className="bill-details">
										Rs.{" "}
										{rupeeConverter(
											formik.values?.sub_total
										)}
									</div>
								</div>
							</div>
						))}
					</div>
				) : (
					<EmptyRecord />
				)}

				<div className="divider" />
				<div className="section-header-row">
					<h2 className="section-header">Draft Details</h2>
					<div
						className="section-accordion-btn"
						onClick={() => setHideDraftDetails((prev) => !prev)}
					>
						{hideDraftDetails ? "Show Details" : "Hide"}
					</div>
				</div>
				{hideDraftDetails ? (
					<div className="section-description">
						Total Bill Amount:{" "}
						<span className="section-header">
							{/* totalSales */}
							Rs. {rupeeConverter(formik.values?.total_sales)}
						</span>
					</div>
				) : (
					<div className="client-draft-wrapper">
						<p className="section-description">
							Choose invoice date to create draft
						</p>
						<div className="input-item">
							<p className="input-label">Sales Date</p>
							<InputDatePicker
								value={
									formik.values.sales_date
										? new Date(formik.values.sales_date)
										: null
								}
								onChange={(result) =>
									formik.setFieldValue(
										"sales_date",
										formatEnDate(result.en, "YYYY-MM-DD"),
									)
								}
								maxDate={new Date()}
								clearable
								onClear={() => formik.setFieldValue("sales_date", "")}
								error={
									formik.submitCount > 0
										? formik.errors?.sales_date
										: undefined
								}
							/>
						</div>

						<p className="section-description">
							Choose the mode of payment
						</p>
						<div className="input-item">
							<p className="input-label">Payment Method</p>
							<SelectBox
								name="payment_mode"
								onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
									handleInvoiceDateChange(
										"payment_mode",
										e.target.value
									)
								}
								onBlur={formik.handleBlur}
								value={formik.values?.payment_mode || ""}
								options={strings.paymentMode}
							/>
							{formik.submitCount > 0 && (
								<div className="validation-error">
									{formik?.errors?.payment_mode}
								</div>
							)}
						</div>
						{/* part from invoice form component with style */}
						<div
							className="invoice-form-container"
							style={{ padding: "0px", fontSize: "10px" }}
						>
							<InvoiceItemsFooter
								formik={formik}
								onDiscountServiceChange={
									handleDiscountServiceChange
								}
								onBilledItemsChange={handleBilledItemsChange}
								onServiceChargeToggle={
									handleServiceChargeToggle
								}
								isServiceChargeEnabled={isServiceChargeEnabled}
								onFieldValueChange={(fieldName: string, fieldValue: unknown) =>
									formik.setFieldValue(fieldName, fieldValue)
								}
								preferenceDetails={preferenceDetails}
							/>
						</div>
						{/* part from invoice form component with style */}
						<div
							className="action-container"
							style={{ marginTop: "-16px" }}
						>
							<div
								className="action-btn"
								style={{ margin: "0" }}
								onClick={formik.submitForm}
							>
								<FaFloppyDisk />
								Save Details
							</div>
						</div>
					</div>
				)}
			</div>
			)}
		</div>
		</StructuredSideModal>
	);
}

export default CustomerBulkDraft;
